import { Checkbox, FormControlLabel } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { ModuleCatalogEntry, RoleListItem, RoleModulePermission, SaveRolePermissionsPayload } from "../types.tsx";
import { StandardModal } from "../table-page/StandardModal.tsx";

type ModulePermissionDraft = RoleModulePermission;

const emptyCrud = {
  canCreate: false,
  canRead: false,
  canUpdate: false,
  canDelete: false,
};

function fromRole(
  role: RoleListItem | null | undefined,
  catalog: ModuleCatalogEntry[],
): Map<string, ModulePermissionDraft> {
  const modules = new Map<string, ModulePermissionDraft>();
  for (const entry of catalog) {
    const existing = role?.modulePermissions?.find((p) => p.module === entry.key);
    modules.set(entry.key, {
      module: entry.key,
      canCreate: existing?.canCreate ?? false,
      canRead: existing?.canRead ?? false,
      canUpdate: existing?.canUpdate ?? false,
      canDelete: existing?.canDelete ?? false,
    });
  }
  return modules;
}

function hasAnyCrud(p: {
  canCreate: boolean;
  canRead: boolean;
  canUpdate: boolean;
  canDelete: boolean;
}) {
  return p.canCreate || p.canRead || p.canUpdate || p.canDelete;
}

export type RolePermissionsModalProps = {
  open: boolean;
  role?: RoleListItem | null;
  moduleCatalog: ModuleCatalogEntry[];
  loading?: boolean;
  serverError?: string | null;
  onClose: () => void;
  onSubmit: (payload: SaveRolePermissionsPayload) => void;
};

export function RolePermissionsModal({
  open,
  role,
  moduleCatalog,
  loading,
  serverError,
  onClose,
  onSubmit,
}: RolePermissionsModalProps) {
  const [moduleDrafts, setModuleDrafts] = useState<Map<string, ModulePermissionDraft>>(() => new Map());
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setModuleDrafts(fromRole(role, moduleCatalog));
    setValidationError(null);
  }, [open, role, moduleCatalog]);

  const patchModule = useCallback((moduleKey: string, patch: Partial<Omit<ModulePermissionDraft, "module">>) => {
    setModuleDrafts((prev) => {
      const existing = prev.get(moduleKey) ?? {
        module: moduleKey as ModulePermissionDraft["module"],
        ...emptyCrud,
      };
      const next = new Map(prev);
      next.set(moduleKey, { ...existing, ...patch });
      return next;
    });
  }, []);

  const submit = () => {
    const modulePermissions = Array.from(moduleDrafts.values()).filter(hasAnyCrud);
    if (modulePermissions.length === 0) {
      setValidationError("Enable at least one module permission.");
      return;
    }
    setValidationError(null);
    onSubmit({ modulePermissions });
  };

  const footer = useMemo(
    () => (
      <PermissionsModalFooter loading={loading} onClose={onClose} onSubmit={submit} />
    ),
    [loading, onClose, submit],
  );

  return (
    <StandardModal
      open={open}
      title={`Permissions — ${role?.role ?? ""}`}
      onClose={onClose}
      footer={footer}
      loading={loading}
      maxWidth="md"
    >
      <div className="ds-stack ds-stack--loose" style={{ width: "100%" }}>
        {serverError || validationError ? (
          <p className="ds-error-inline" style={{ margin: 0 }}>
            {serverError ?? validationError}
          </p>
        ) : null}

        <section>
          <h3 className="ds-perm-section-title">Module access</h3>
          <div className="ds-perm-module-grid">
            <div className="ds-perm-module-row ds-perm-module-row--head">
              <span>Module</span>
              <span>All</span>
              <span>Create</span>
              <span>Read</span>
              <span>Update</span>
              <span>Delete</span>
            </div>
            {moduleCatalog.map((entry) => {
              const draft = moduleDrafts.get(entry.key) ?? { module: entry.key, ...emptyCrud };
              const any = hasAnyCrud(draft);
              const all = draft.canCreate && draft.canRead && draft.canUpdate && draft.canDelete;
              return (
                <div key={entry.key} className="ds-perm-module-row">
                  <span className="ds-perm-module-label">{entry.label}</span>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={all}
                        indeterminate={any && !all}
                        onChange={(_, c) =>
                          patchModule(entry.key, {
                            canCreate: c,
                            canRead: c,
                            canUpdate: c,
                            canDelete: c,
                          })
                        }
                      />
                    }
                    label=""
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={draft.canCreate}
                        onChange={(_, c) => patchModule(entry.key, { canCreate: c })}
                      />
                    }
                    label=""
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={draft.canRead}
                        onChange={(_, c) => patchModule(entry.key, { canRead: c })}
                      />
                    }
                    label=""
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={draft.canUpdate}
                        onChange={(_, c) => patchModule(entry.key, { canUpdate: c })}
                      />
                    }
                    label=""
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={draft.canDelete}
                        onChange={(_, c) => patchModule(entry.key, { canDelete: c })}
                      />
                    }
                    label=""
                  />
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </StandardModal>
  );
}

function PermissionsModalFooter({
  loading,
  onClose,
  onSubmit,
}: {
  loading?: boolean;
  onClose: () => void;
  onSubmit: () => void;
}) {
  return (
    <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
      <button type="button" className="ds-btn ds-btn--ghost" onClick={onClose} disabled={loading}>
        Cancel
      </button>
      <button type="button" className="ds-btn ds-btn--primary" onClick={onSubmit} disabled={loading}>
        Save
      </button>
    </div>
  );
}
